import React, { useMemo } from 'react';

import AbsenceCategoriesFilter from './AbsenceCategoriesFilter';
import ServerError from '../../../../../../../components/ServerError';
import OfficeFilter from './OfficeFilter';
import {
  ManagerRole,
  useTeamCalendarFiltersQuery,
} from '../../../../../__generated__/graphql';
import TeamFilter from './TeamFilter';
import AbsentStatusFilter from './AbsentStatusFilter';
import { TeamCalendarFiltersType } from './types';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';
import DepartmentFilter from './DepartmentFilter';
import { FilterSelectOption } from '../../../../../../../atomic/molecules/FlairDropdown/types';
import ClearFilters from '../../../../../components/ClearFilters';
import { routes } from '../../../../../routes';
import { useQueryParams } from '../../../../../../../hooks/useQueryParams';
import { getUniqueGroupsByCategories } from '../../../GroupCategories';
import { useIsManagerMode } from '../../../../../hooks/useIsManagerMode';
import {
  extractDepartmentOptions,
  extractLocationOptions,
  extractTeamOptions,
} from '../../../../../utils/employeeFilterOptions';

type Props = {
  filterData: TeamCalendarFiltersType;
  from: string;
  to: string;
};

const FilterItem: React.FC<
  React.PropsWithChildren<{
    className?: string;
    children: React.ReactNode;
  }>
> = ({ className = '', children }) => {
  return <div className={`${className}`}>{children}</div>;
};

export const specialTeams = {
  my_team: 'my_team',
  all_teams: 'all_teams',
  subordinates: 'subordinates',
} as const;

export type SpecialTeam = (typeof specialTeams)[keyof typeof specialTeams];

const TeamCalendarFilters: React.FC<React.PropsWithChildren<Props>> = ({
  filterData,
  from,
  to,
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  const { data, loading, error } = useTeamCalendarFiltersQuery({
    variables: {
      from,
      to,
    },
  });

  const { hasAnyOfManagerRole } = useIsManagerMode();
  const route = routes.teamCalendar.route
    .withQueryParams({
      startDate: queryParams['startDate'] || '',
    })
    .create({});

  const officeOptions: FilterSelectOption[] = useMemo(() => {
    if (!data?.me?.teamColleagues) return [];
    return extractLocationOptions(data.me.teamColleagues);
  }, [data]);

  const departmentOptions: FilterSelectOption[] = useMemo(() => {
    if (!data?.me?.teamColleagues) return [];
    return extractDepartmentOptions(data.me.teamColleagues);
  }, [data]);

  const teams: FilterSelectOption[] = useMemo(() => {
    if (!data?.me?.teamColleagues) return [];
    return extractTeamOptions(data.me.teamColleagues);
  }, [data]);

  if (loading) return <Loading />;

  if (error || data === undefined) return <ServerError />;

  let absenceCategoriesOptions = data.absencesCategories
    .map((a) => ({
      label: a.Name,
      value: a.Id,
      public: a.flair__Public__c,
      archived: a.flair__Archived_Date__c !== null,
    }))
    .filter((category) => category.public && !category.archived);

  const categoriesGroups = getUniqueGroupsByCategories(
    data.absencesCategories,
  ).map((g) => ({
    label: t('categoriesGroup.groupName', { group_name: g.name }),
    value: g.id,
    public: true,
  }));

  let absenceCategoriesWithGroupsOptions = [
    ...categoriesGroups,
    ...absenceCategoriesOptions,
  ];

  if (data.absencesCategories.find((category) => !category.flair__Public__c))
    absenceCategoriesWithGroupsOptions = [
      ...absenceCategoriesWithGroupsOptions,
      {
        label: t('employeePage.teamCalendar.filters.absenceCategories.Absent'),
        value: 'ABSENT',
        public: false,
      },
    ];

  const teamFilterOptions: FilterSelectOption[] = [
    {
      label: t('employeePage.teamCalendar.filters.team.options.my_team'),
      value: specialTeams.my_team,
    },
    {
      label: t('employeePage.teamCalendar.filters.team.options.all_teams'),
      value: specialTeams.all_teams,
    },
    {
      label: t('employeePage.teamCalendar.filters.team.options.reports'),
      value: specialTeams.subordinates,
      showCondition: hasAnyOfManagerRole([ManagerRole.DirectManager]),
    },
    ...teams,
  ];

  const absentStatusFilterOptions: FilterSelectOption[] = [
    {
      label: t(
        'employeePage.teamCalendar.filters.absentStatus.options.absent_today',
      ),
      value: 'absent_today',
    },
    {
      label: t(
        'employeePage.teamCalendar.filters.absentStatus.options.absent_this_month',
      ),
      value: 'absent_this_month',
    },
  ];

  return (
    <div className="d-none d-md-flex align-items-center flex-wrap gap-3">
      <span className="text-nowrap d-none d-lg-block">
        {t('employeePage.teamCalendar.filterBy')}
      </span>
      <FilterItem>
        <OfficeFilter filterData={filterData} options={officeOptions} />
      </FilterItem>
      <FilterItem>
        <DepartmentFilter filterData={filterData} options={departmentOptions} />
      </FilterItem>
      <FilterItem>
        <TeamFilter filterData={filterData} options={teamFilterOptions} />
      </FilterItem>
      <FilterItem>
        <AbsenceCategoriesFilter
          filterData={filterData}
          options={absenceCategoriesWithGroupsOptions}
        />
      </FilterItem>
      <FilterItem>
        <AbsentStatusFilter
          filterData={filterData}
          options={absentStatusFilterOptions}
        />
      </FilterItem>
      <FilterItem>
        <ClearFilters route={route} />
      </FilterItem>
    </div>
  );
};

export default TeamCalendarFilters;
